import { Button, Chip, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material";
import React from "react";
import { ModalContext } from "../../../../Components/Modals";
import { GridRowsProp, GridRowParams, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { orderService, setupService } from "../../../../Core/Api/services";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import { UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { DbDeliveryBroadcasterInfos } from "../../../../Core/Api/Types/db-delivery-broadcaster-infos";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { OrderProductType } from "../../../../Core/Api/Types/order-product-type";
import ModalBroadcaster, {
  ModalBroadcasterElementProps,
} from "../../../../Components/Modals/ModalBroadcaster/modal-broadcaster";
import { TranskodeVideoProfile } from "../../../../Core/Api/Types/transkode-video-profile";
import { useTranslation } from "react-i18next";
import { WorkspacesOutlined } from "@mui/icons-material";
import TableUuids from "../../../../Components/Table/table-uuids";

function SettingsBroadcasters() {
  const { t } = useTranslation();
  // Loading
  const [isLoading, setIsLoading] = React.useState(true);
  // Role filter
  const [typeFilter, setTypeFilter] = React.useState<OrderProductType>();
  const [searchFilter, setSearchFilter] = React.useState<string>("");
  const enqueueSnackbar = useCustomSnackbar();
  // Table rows
  const [rows, setRows] = React.useState<DbDeliveryBroadcasterInfos[]>([]);
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const apiGrid = useGridApiRef();

  const aclSuperUser = UserHasAcl(AclTypes.aclSuperUser);

  const handleNewBroadcasterClick = (broadcaster?: DbDeliveryBroadcasterInfos) => {
    if (!openModal) {
      return;
    }

    const onClose: ModalBroadcasterElementProps["onClose"] = (data) => {};

    openModal<ModalBroadcasterElementProps>(ModalBroadcaster, {
      type: "Create",
      inputBroadcaster: broadcaster,
      onConfirmModalBroadcaster: onAddBroadcaster,
      onClose,
    });
  };

  const handleModifyBroadcasterClick = (broadcaster: DbDeliveryBroadcasterInfos) => {
    if (!openModal) {
      return;
    }

    const onClose: ModalBroadcasterElementProps["onClose"] = (data) => {};

    openModal<ModalBroadcasterElementProps>(ModalBroadcaster, {
      type: "Modify",
      inputBroadcaster: broadcaster,
      onConfirmModalBroadcaster: onModifyBroadcaster,
      onClose,
    });
  };

  const onAddBroadcaster = (broadcaster: DbDeliveryBroadcasterInfos, id?: number) => {
    setRows([
      ...rows,
      new DbDeliveryBroadcasterInfos({
        description: broadcaster.description,
        documentsFolder: broadcaster.documentsFolder,
        enabled: broadcaster.enabled,
        externalId: broadcaster.externalId,
        id: id,
        identityProfiles: broadcaster.identityProfiles,
        isAutomatedPipeline: broadcaster.isAutomatedPipeline,
        languagesCodes: broadcaster.languagesCodes,
        localizationTerritoryCode: broadcaster.localizationTerritoryCode,
        name: broadcaster.name,
        productType: broadcaster.productType,
        serviceDescription: broadcaster.serviceDescription,
        territoriesCodes: broadcaster.territoriesCodes,
      }),
    ]);
  };

  const onModifyBroadcaster = (broadcaster: DbDeliveryBroadcasterInfos) => {
    setRows([
      ...rows.map((x) => {
        if (x.id === broadcaster.id) return broadcaster;
        else return x;
      }),
    ]);
  };

  const handleChangeTypeSelect = (type: OrderProductType) => {
    setTypeFilter(type);
  };

  const onClickDownload = (id: number) => {
    orderService.DownloadBroadcasterDocuments(id).then(function () {
      console.debug("download done!");
    });
  };

  const onClickDelete = (id: number) => {
    setupService.DeleteBroadcaster(id).then(function (response) {
      if (response.success) {
        enqueueSnackbar(t("alert:success.broadcasterDeleted"), "success");
        setRows(rows.filter((row) => row.id !== id));
      } else enqueueSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
    });
  };

  // #region Grid definition
  let _rows: GridRowsProp = [];
  type Row = (typeof _rows)[number];

  const BroadcastersColumns: GridColDef[] = [
    {
      headerName: "Id",
      field: "id",
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (data: Row) => data.value,
    },
    {
      headerName: t("table:headerName.externalIdOrSapCode"),
      field: "externalId",
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: Row) => data.value,
    },
    {
      headerName: t("common:name"),
      field: "name",
      flex: CellSize.large,
      minWidth: CellSize.large,
      renderCell: (data: Row) => <Typography sx={{ mt: 1.5, mb: 1.5 }}>{data.value}</Typography>,
    },
    {
      headerName: t("table:headerName.videoProfiles"),
      field: "identityProfiles.videoProfile",
      flex: CellSize.large,
      minWidth: CellSize.large,
      type: "singleSelect",
      headerAlign: "center",
      align: "center",
      valueOptions: () => Object.keys(TranskodeVideoProfile).map((x) => x),
      valueGetter: (data: any, row: any) => (!row ? [] : row["identityProfiles"]?.map((x) => x.videoProfile) || []),
      renderCell: (data: Row) => (
        <Stack spacing={1} direction={"row"} maxWidth={295} sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }}>
          {data.row["identityProfiles"]?.map((x) => (
            <Tooltip title={x.videoProfile} key={x.videoProfile}>
              <Chip label={x.videoProfile} key={x.id} size="small" />
            </Tooltip>
          ))}
        </Stack>
      ),
    },
    {
      headerName: t("field:label.productTypeLowerCase"),
      field: "productType",
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      type: "singleSelect",
      valueOptions: () => Object.keys(OrderProductType).map((x) => x),
      renderCell: (data: Row) => <Chip label={data.value} color="primary" size="small" />,
    },
    {
      headerName: t("table:headerName.validIdentities"),
      field: "identityProfiles",
      flex: CellSize.xxlarge,
      minWidth: CellSize.xxlarge,
      type: "boolean",
      headerAlign: "left",
      valueGetter: (data: any, row: any) =>
        !row ? "" : row["identityProfiles"] !== null && row["identityProfiles"]?.every((x) => !x.isValidParsing),
      renderCell: (data: Row) => (
        <Stack
          alignItems={"center"}
          width={"100%"}
          direction={"column"}
          spacing={1}
          sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }}
        >
          {data.row["identityProfiles"]?.map((x) => (
            <Stack width={"100%"} direction={"row"} spacing={2} display={"flex"}>
              <Tooltip title={x.invalidMessage} key={x.id}>
                <Chip
                  label={x.videoProfile + (x.invalidMessage ? " - " + x.invalidMessage : "")}
                  color={x.isValidParsing ? "success" : "error"}
                  size="small"
                  sx={{ width: "38px", mr: 1 }}
                />
              </Tooltip>

              <Stack textAlign={"center"} flex={1.5} direction={"column"}>
                <Typography variant="body2" color={"text.tier"}>
                  {x.user && t("field:label.username")}
                </Typography>
                {x.user && (
                  <Typography sx={{ wordBreak: "break-all" }} variant="body2">
                    {x.user}
                  </Typography>
                )}
              </Stack>

              <Stack textAlign={"center"} flex={1.5} direction={"column"}>
                <Typography variant="body2" color={"text.tier"}>
                  {x.password && t("common:password")}
                </Typography>
                {x.password && (
                  <Typography sx={{ wordBreak: "break-all" }} variant="body2">
                    {x.password}
                  </Typography>
                )}
              </Stack>

              <Stack textAlign={"center"} flex={1} direction={"column"}>
                <Typography variant="body2" color={"text.tier"}>
                  {x.method && t("common:method")}
                </Typography>
                {x.method && (
                  <Typography sx={{ wordBreak: "break-all" }} variant="body2">
                    {x.method}
                  </Typography>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      ),
    },
    {
      headerName: t("common:enabled"),
      field: "enabled",
      flex: CellSize.small,
      minWidth: CellSize.small,
      type: "boolean",
      renderCell: (data: Row) => <TableCellBool value={data.value} />,
    },
    {
      headerName: t("field:label.automated"),
      field: "isAutomatedPipeline",
      flex: CellSize.small,
      minWidth: CellSize.small,
      type: "boolean",
      renderCell: (data: Row) => <TableCellBool value={data.value} />,
    },
    {
      headerName: t("common:email_other"),
      field: "emails",
      flex: CellSize.large,
      minWidth: CellSize.large,
      renderCell: (data: Row) => (
        <Stack direction={"column"} spacing={1}>
          {!data.value ? (
            <></>
          ) : data.value.length < 4 ? (
            data.value?.map((x) => <Typography>{x}</Typography>)
          ) : (
            <Tooltip
              title={
                <Stack direction={"column"} spacing={1}>
                  {data.value?.map((x) => (
                    <Typography>{x}</Typography>
                  ))}
                </Stack>
              }
            >
              <Chip label={`${data.value.length} ${t("common:email_other")}`} />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      flex: CellSize.actionsSmall,
      minWidth: CellSize.actionsSmall,
      align: "right",
      getActions: (params: GridRowParams) => {
        if (!params || !params.row) return [];
        let buttons: any[] = [];

        buttons.push(
          <GridActionsCellItem
            icon={<FileDownloadOutlinedIcon sx={{ width: 20, height: 20, color: "text.secondary" }} />}
            label={t("common:download")}
            onClick={() => onClickDownload(params.row["id"])}
            showInMenu
          />
        );

        if (aclSuperUser)
          buttons.push(
            <GridActionsCellItem
              icon={<ContentCopyOutlinedIcon sx={{ width: 20, height: 20, color: "text.secondary" }} />}
              label={t("common:copy")}
              onClick={() => handleNewBroadcasterClick(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              icon={<EditOutlinedIcon sx={{ width: 20, height: 20, color: "primary.text" }} />}
              label={t("common:modify")}
              onClick={() => handleModifyBroadcasterClick(params.row)}
              showInMenu
            />,
            <GridActionsCellItem
              icon={<DeleteOutlinedIcon sx={{ width: 20, height: 20, color: "error.main" }} />}
              label={t("common:delete")}
              onClick={() => onClickDelete(params.row["id"])}
              showInMenu
            />
          );

        return buttons;
      },
    },
  ];
  // #endregion

  React.useEffect(() => {
    orderService.GetBroadcastersList().then(function (response) {
      if (response.success) {
        setRows(response.broadcasters || []);
      }
      setIsLoading(false);
    });
  }, []);

  const additionalActions = (
    <Button
      variant="contained"
      color="primary"
      startIcon={<AddIcon />}
      onClick={() => handleNewBroadcasterClick()}
      disabled={!UserHasAcl(AclTypes.aclBackofficeUserCreate)}
    >
      {t("button:newBroadcaster")}
    </Button>
  );

  const additionalFilters = (
    <Select
      displayEmpty
      IconComponent={ExpandMoreRoundedIcon}
      startAdornment={<WorkspacesOutlined />}
      MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
      value={typeFilter}
      onChange={(e) => handleChangeTypeSelect(OrderProductType[e.target.value])}
    >
      <MenuItem value={undefined} selected>
        {t("field:label.productType")}
      </MenuItem>
      {Object.values(OrderProductType)
        .sort()
        .map((type) => (
          <MenuItem value={type} key={type}>
            {type}
          </MenuItem>
        ))}
    </Select>
  );

  return (
    <DataTable
      loading={isLoading}
      tableKey={TableUuids.SETTINGS_BROADCASTERS_TABLE}
      columns={BroadcastersColumns}
      rows={rows.filter((row) => (typeFilter ? row.productType === typeFilter : row))}
      additionalActions={additionalActions}
      additionalFilters={additionalFilters}
      getRowHeight={() => "auto"}
      pagination={true}
      initialState={{
        sorting: {
          sortModel: [{ field: "name", sort: "asc" }],
        },
      }}
      disableRowSelectionOnClick
      apiRef={apiGrid}
    />
  );
}

export default SettingsBroadcasters;
